import { isNative } from '@/lib/platform';

/**
 * Dynamically import the Capacitor Local Notifications plugin.
 * Returns null if the package is not installed (optional dependency).
 */
async function getLocalNotifications(): Promise<{
  readonly requestPermissions: () => Promise<{ readonly display: string }>;
  readonly schedule: (options: { readonly notifications: readonly Record<string, unknown>[] }) => Promise<void>;
} | null> {
  try {
    const moduleName = '@capacitor/local-notifications';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const mod: any = await import(/* @vite-ignore */ moduleName);
    return mod.LocalNotifications ?? null;
  } catch {
    return null;
  }
}

/**
 * Request browser notification permission.
 * On native platforms, permissions are handled by the Capacitor plugin.
 */
export async function requestNotificationPermission(): Promise<boolean> {
  if (isNative()) {
    try {
      const plugin = await getLocalNotifications();
      if (!plugin) return false;
      const result = await plugin.requestPermissions();
      return result.display === 'granted';
    } catch {
      return false;
    }
  }

  if (!('Notification' in window)) return false;
  if (Notification.permission === 'granted') return true;
  if (Notification.permission === 'denied') return false;

  const result = await Notification.requestPermission();
  return result === 'granted';
}

/**
 * Show a local notification using the browser Notification API or
 * Capacitor LocalNotifications on native platforms.
 */
export function showLocalNotification(
  title: string,
  body: string,
  onClick?: () => void,
): void {
  if (isNative()) {
    showNativeNotification(title, body);
    return;
  }

  if (Notification.permission !== 'granted') return;

  const notification = new Notification(title, {
    body,
    icon: '/icon-192.png',
    badge: '/icon-192.png',
  });

  if (onClick) {
    notification.onclick = () => {
      onClick();
      notification.close();
    };
  }
}

/** Schedule a Capacitor local notification (fire-and-forget). */
async function showNativeNotification(title: string, body: string): Promise<void> {
  try {
    const plugin = await getLocalNotifications();
    if (!plugin) return;
    await plugin.schedule({
      notifications: [
        {
          id: Date.now(),
          title,
          body,
          schedule: { at: new Date(Date.now() + 100).toISOString() },
          smallIcon: 'ic_notification',
        },
      ],
    });
  } catch {
    // Plugin not available — ignore silently
  }
}
